<template>
  <div class="container mt-4">
    <h2 class="mb-4">
      <font-awesome-icon icon="leaf" class="me-2" /> Plantas del Lote
    </h2>

    <!-- Botonera principal -->
    <div class="d-flex flex-wrap gap-3 mb-4">
      <button type="button" @click="toggleAltaPlantaLote" class="btn btn-primary" :disabled="lote_bloqueado || plantas.length >200 || proyectoCerrado">
        <font-awesome-icon icon="plus" class="me-2" /> Agregar Plantas
      </button>
      <button type="button" @click="openChangeStateAllModal" class="btn btn-secondary" :disabled=" plantas.length === 0 || proyectoCerrado">
        <font-awesome-icon :icon="['fas', 'seedling']" class="me-2" /> Cambiar Estado de Todas
      </button>
    </div>

    <!-- Vista en Tabla con Filtros, Ordenamiento y Paginado -->
    <div v-if="!mostrarAltaPlantaLote">
      <div v-if="plantas.length === 0" class="alert alert-info">
        No hay plantas registradas en este lote.
      </div>
      <div v-else>
        <!-- Resumen de cantidad de plantas por estado (ordenado de mayor a menor) -->
        <div class="mb-3">
          <p>
            <strong>Cantidad de Plantas por Estado:</strong>
            <span v-for="(item, index) in plantasPorEstadoOrdenadas" :key="index" class="badge bg-secondary me-1">
              {{ item.estado }}: {{ item.cantidad }}
            </span>
          </p>
        </div>

        <!-- Filtros -->
        <div class="card p-3 shadow-sm bg-white rounded mb-4">
          <div class="row">
            <div class="col-md-4">
              <label for="filtroEstado" class="form-label">Filtrar por Estado</label>
              <select id="filtroEstado" v-model="filtroEstado" class="form-select">
                <option value="">Todos</option>
                <option v-for="estado in estados" :key="estado" :value="estado">{{ estado }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label for="filtroFechaBaja" class="form-label">Filtrar por Fecha de Baja</label>
              <select id="filtroFechaBaja" v-model="filtroFechaBaja" class="form-select">
                <option value="">Todos</option>
                <option value="true">Con Fecha de Baja</option>
                <option value="false">Sin Fecha de Baja</option>
              </select>
            </div>
            <div class="col-md-4">
              <label for="searchText" class="form-label">Buscar por Nombre</label>
              <input type="text" id="searchText" v-model="searchText" class="form-control" placeholder="Buscar..." />
            </div>
          </div>
        </div>

        <!-- Tabla de Plantas -->
        <div v-if="paginatedPlantas.length === 0" class="alert alert-info">
          No hay plantas que coincidan con los filtros.
        </div>
        <div v-else class="table-responsive mb-3">
          <table class="table table-striped table-hover align-middle">
            <thead>
              <tr>
                <th @click="ordenar('nombre')" class="sortable">
                  Nombre <font-awesome-icon :icon="iconoOrden('nombre')" />
                </th>
                <th @click="ordenar('estado')" class="sortable">
                  Estado <font-awesome-icon :icon="iconoOrden('estado')" />
                </th>
                <th @click="ordenar('origen')" class="sortable">
                  origen <font-awesome-icon :icon="iconoOrden('origen')" />
                </th>
                <th @click="ordenar('fecha_inicio')" class="sortable">
                  Fecha de Inicio <font-awesome-icon :icon="iconoOrden('fecha_inicio')" />
                </th>
                <th @click="ordenar('fecha_baja')" class="sortable">
                  Fecha Baja <font-awesome-icon :icon="iconoOrden('fecha_baja')" />
                </th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="planta in paginatedPlantas" :key="planta.id">
                <td>{{ planta.codigo }}</td>
                <td>{{ planta.estado }}</td>
                <td>{{ planta.origen }}</td>
                <td>{{ planta.fecha_inicio }}</td>
                <td>
                  <span v-if="planta.fecha_baja" class="text-danger">{{ planta.fecha_baja }}</span>
                  <span v-else>-</span>
                </td>
                <td class="text-end">
                  <!-- Botón de dropdown para acciones -->
                  <div class="dropdown">
                    <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                      <font-awesome-icon icon="ellipsis-v" />
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li>
                        <a class="dropdown-item" href="#" @click.prevent="verDetalles(planta)">
                          <font-awesome-icon icon="eye" class="me-2" /> Ver
                        </a>
                      </li>
                      <li>
                        <a class="dropdown-item" href="#" @click.prevent="openChangeStateModal(planta)" :class="{ disabled: planta.fecha_baja || proyectoCerrado }">
                          <font-awesome-icon icon="exchange-alt" class="me-2" /> Cambiar Estado
                        </a>
                      </li>
                      <li>
                        <a class="dropdown-item" href="#" @click.prevent="borrarPlanta(planta.id)" :class="{ disabled: lote_bloqueado || proyectoCerrado }"> 
                          <font-awesome-icon icon="trash-alt" class="me-2" /> Borrar
                        </a>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginado -->
        <nav v-if="totalPaginas > 1" aria-label="Paginación">
          <ul class="pagination justify-content-center">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" type="button" @click="cambiarPagina(currentPage - 1)">
                <font-awesome-icon icon="chevron-left" />
              </button>
            </li>
            <li class="page-item" v-for="page in totalPaginas" :key="page" :class="{ active: page === currentPage }">
              <button class="page-link" type="button" @click="cambiarPagina(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPaginas }">
              <button class="page-link" type="button" @click="cambiarPagina(currentPage + 1)">
                <font-awesome-icon icon="chevron-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Modal para Cambiar Estado de una Planta Individual -->
    <ChangeStateModal
      v-if="showChangeStateModal"
      :visible="showChangeStateModal"
      :title="`Cambiar Estado de Planta: ${plantaSeleccionada.codigo}`"
      :estados="estados"
      :value="nuevoEstadoPlanta"
      :warning="''"
      @cancel="closeChangeStateModal"
      @confirm="confirmChangeStatePlant"
    />

    <!-- Modal para Cambiar Estado de Todas las Plantas Activas -->
    <ChangeStateModal
      v-if="showChangeStateAllModal"
      :visible="showChangeStateAllModal"
      title="Cambiar Estado de Todas las Plantas Activas"
      :estados="estados"
      :value="nuevoEstadoTodas"
      warning="Solo se modificarán las plantas activas (sin fecha de baja)."
      @cancel="closeChangeStateAllModal"
      @confirm="confirmChangeStateAll"
    />

    <!-- Modal para Ver Detalles de la Planta -->
    <div class="modal fade show" tabindex="-1" aria-labelledby="plantaModalLabel" role="dialog" v-if="plantaSeleccionada && showDetailsModal" :style="{ display: showDetailsModal ? 'block' : 'none' }">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalles de la Planta: {{ plantaSeleccionada.nombre }}</h5>
            <button type="button" class="btn-close" @click="closeDetailsModal"></button>
          </div>
          <div class="modal-body">
            <p><strong>Variedad:</strong> {{ plantaSeleccionada.variedad.nombre }} (THC: {{ plantaSeleccionada.variedad.thc }}% / CBD: {{ plantaSeleccionada.variedad.cbd }}%)</p>
            <p><strong>Origen:</strong> {{ plantaSeleccionada.origen }}</p>
            <p><strong>Estado:</strong> {{ plantaSeleccionada.estado }}</p>
            <p><strong>Fecha de Inicio:</strong> {{ plantaSeleccionada.fecha_inicio }}</p>
            <p><strong>Descripción:</strong> {{ plantaSeleccionada.descripcion }}</p>
            <div class="text-center">
              <div id="qrContainer" class="qr-section">
                <QRCode :value="qrData" :size="150" />
                <p><strong>{{ plantaSeleccionada.nombre }}</strong></p>
                <p>{{ plantaSeleccionada.variedad.nombre }} (THC: {{ plantaSeleccionada.variedad.thc }}% / CBD: {{ plantaSeleccionada.variedad.cbd }}%)</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="descargarQR">Descargar QR</button>
          </div>
        </div>
      </div>
    </div>

        <!-- Modal para Crear Planta -->
        <AltaPlantaLote
          v-if="mostrarAltaPlantaLote"
          :lote_id="lote_id"
          :club_id="club_id"
          :proyecto_id="proyecto_id"
          @close="ocultarAltaPlantaLote"
        />
        

  </div>
</template>

<script>
import axios from '@/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AltaPlantaLote from '@/components/Planta/alta-plantaLote.vue';
import QRCode from 'qrcode.vue';
import html2canvas from 'html2canvas';
import ChangeStateModal from '@/components/Planta/ChangeStateModal.vue';

export default {
  components: {
    AltaPlantaLote,
    FontAwesomeIcon,
    QRCode,
    ChangeStateModal,
  },
  props: {
    lote_id: { type: Number, required: true },
    lote_bloqueado: { type: Boolean, required: true },
    proyecto_id: { type: Number, required: true },
  },
  watch: {
    lote_id() {
      this.cargarPlantas();
    },
  },
  data() {
    return {
      club_id: this.$store.getters.getClubActual?.id || null,
      plantas: [],
      mostrarAltaPlantaLote: false,
      plantaSeleccionada: null,
      qrData: '',
      showChangeStateModal: false,
      showChangeStateAllModal: false,
      showDetailsModal: false,
      nuevoEstadoTodas: "",
      nuevoEstadoPlanta: "",
      estados: [],
      // Filtros
      filtroEstado: "",
      filtroFechaBaja: "",
      searchText: "",
      // Ordenamiento y paginado
      sortColumn: "nombre",
      sortOrder: "asc",
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    filteredPlantas() {
      return this.plantas.filter(planta => {
        const matchesEstado = this.filtroEstado ? planta.estado === this.filtroEstado : true;
        const matchesSearch = this.searchText ? planta.codigo.toLowerCase().includes(this.searchText.toLowerCase()) : true;
        const matchesFechaBaja = this.filtroFechaBaja === "true" ? planta.fecha_baja : this.filtroFechaBaja === "false" ? !planta.fecha_baja : true;
        return matchesEstado && matchesSearch && matchesFechaBaja;
      });
    },
    sortedPlantas() {
      return [...this.filteredPlantas].sort((a, b) => {
        let result = 0;
        if (a[this.sortColumn] < b[this.sortColumn]) result = -1;
        if (a[this.sortColumn] > b[this.sortColumn]) result = 1;
        return this.sortOrder === "asc" ? result : -result;
      });
    },
    paginatedPlantas() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedPlantas.slice(start, start + this.itemsPerPage);
    },
    totalPaginas() {
      return Math.ceil(this.sortedPlantas.length / this.itemsPerPage);
    },
    plantasPorEstadoOrdenadas() {
      const counts = this.plantas.reduce((acc, planta) => {
        acc[planta.estado] = (acc[planta.estado] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(counts)
        .map(([estado, cantidad]) => ({ estado, cantidad }))
        .sort((a, b) => b.cantidad - a.cantidad);
    },
  },
  methods: {
    ordenar(columna) {
      if (this.sortColumn === columna) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = columna;
        this.sortOrder = "asc";
      }
      this.currentPage = 1;
    },
    iconoOrden(columna) {
      if (this.sortColumn !== columna) return "sort";
      return this.sortOrder === "asc" ? "sort-up" : "sort-down";
    },
    cambiarPagina(page) {
      if (page < 1 || page > this.totalPaginas) return;
      this.currentPage = page;
    },
    async cargarPlantas() {
      try {
        const response = await axios.get(`/api/planta/lote/${this.lote_id}`);
        this.plantas = response.data;
        this.plantas.forEach(planta => {
          planta.fecha_inicio = planta.fecha_inicio.slice(0, 10);
          planta.fecha_baja = planta.fecha_baja ? planta.fecha_baja.slice(0, 10) : null;
        });
      } catch (error) {
        console.error('Error al cargar las plantas:', error);
      }
    },
    async cargarEstados() {
      try {
        const response = await axios.get("/api/constantes/estados_de_una_planta");
        this.estados = response.data;
      } catch (error) {
        console.error("Error al cargar los estados:", error);
      }
    },
    openChangeStateModal(planta) {
      this.plantaSeleccionada = planta;
      this.nuevoEstadoPlanta = "";
      this.showChangeStateModal = true;
    },
    closeChangeStateModal() {
      this.plantaSeleccionada = null;
      this.showChangeStateModal = false;
    },
    async confirmChangeStatePlant(nuevoEstado) {
      if (nuevoEstado === 'Muerta' || nuevoEstado === 'Descartada') {
        const confirmacion = confirm('¿Estás seguro de que deseas cambiar el estado a "' + nuevoEstado + '"? Esta acción dará de baja la planta y no se podrá volver a usar.');
        if (!confirmacion) {
          return;
        }
      }
      try {
        await axios.put(`/api/planta/${this.plantaSeleccionada.id}/estado`, { nuevoEstado });
        await this.cargarPlantas();
        alert('Estado actualizado para la planta seleccionada.');
        this.closeChangeStateModal();
      } catch (error) {
        console.error('Error al cambiar estado de la planta:', error);
      }
    },
    openChangeStateAllModal() {
      this.nuevoEstadoTodas = "";
      this.showChangeStateAllModal = true;
    },
    closeChangeStateAllModal() {
      this.showChangeStateAllModal = false;
    },
    async confirmChangeStateAll(nuevoEstado) {
      try {
        if (nuevoEstado === 'Muerta' || nuevoEstado === 'Descartada') {
          const confirmacion = confirm('¿Estás seguro de que deseas cambiar el estado a "' + nuevoEstado + '"? Esta acción dará de baja las plantas y no se podrán volver a usar.');
          if (!confirmacion) {
            return;
          }
        }
        const plantasActivas = this.plantas.filter(planta => !planta.fecha_baja);
        if (plantasActivas.length === 0) {
          alert("No hay plantas activas para actualizar.");
          return;
        }
        await axios.put("/api/plantas_estado", {
          lote_id: this.lote_id,
          nuevoEstado,
          plantas: plantasActivas,
        });
        await this.cargarPlantas();
        alert("Estado actualizado para las plantas activas del lote.");
        this.closeChangeStateAllModal();
      } catch (error) {
        console.error("Error al cambiar estado de todas las plantas:", error);
      }
    },
    verDetalles(planta) {
      this.plantaSeleccionada = planta;
      this.qrData = `Nombre: ${planta.codigo}\nVariedad: ${planta.variedad.nombre}\nFecha de Inicio: ${planta.fecha_inicio}`;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.plantaSeleccionada = null;
      this.showDetailsModal = false;
    },
    async descargarQR() {
      const qrElement = document.getElementById('qrContainer');
      if (qrElement) {
        const canvas = await html2canvas(qrElement);
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${this.plantaSeleccionada.nombre}_QR.png`;
        link.click();
      }
    },
    borrarPlanta(planta_id) {
      if (confirm('¿Estás seguro de borrar esta planta?')) {
        axios.delete(`/api/planta/${planta_id}`)
          .then(response => {
            if (response.status === 200) {
              alert('Planta borrada correctamente');
              this.cargarPlantas();
            } else {
              alert('Ocurrió un error al borrar la planta');
            }
          })
          .catch(error => {
            console.error('Error al borrar la planta:', error);
          });
      }
    },
    toggleAltaPlantaLote() {
      this.mostrarAltaPlantaLote = !this.mostrarAltaPlantaLote;
    },
    ocultarAltaPlantaLote() {
      this.mostrarAltaPlantaLote = false;
      this.cargarPlantas();
    }
  },
  mounted() {
    this.proyectoCerrado=this.$store.getters.getProyectoActual?.proyecto_cerrado;
    this.cargarPlantas();
    this.cargarEstados();
  }
};
</script>

<style scoped>
.container {
  max-width: 900px;
}
h2 {
  font-weight: 600;
  font-size: 1.75rem;
}
.btn-primary {
  background-color: var(--app-primary);
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 6px;
  transition: background-color 0.3s ease;
}
.btn-primary:hover {
  background-color: darken(var(--app-primary), 10%);
}
.btn-secondary {
  border-radius: 6px;
  transition: background-color 0.3s ease;
}
.btn-secondary:hover {
  background-color: darken(var(--app-secondary), 10%);
}
.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  border-radius: 6px;
}
.btn-outline-primary,
.btn-outline-danger,
.btn-outline-info {
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem;
  border-radius: 6px;
}
.alert {
  font-size: 1rem;
  padding: 1rem;
  margin-top: 20px;
}
.card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 15px;
  border: 1px solid #ddd;
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease;
}
.card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}
.badge {
  font-size: 0.9rem;
}
.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.3s ease;
}
.sortable:hover {
  color: var(--app-primary);
}
.table {
  margin-bottom: 0;
}
.modal-content {
  border-radius: 10px;
}
.modal-body {
  padding: 2rem;
}
.modal-footer {
  padding: 1rem 2rem;
}
.text-center {
  text-align: center;
}
.qr-section {
  border: 2px solid #28a745;
  border-radius: 8px;
  padding: 10px;
  display: inline-block;
  text-align: center;
  background-color: #fff;
  margin-top: 10px;
  transition: transform 0.3s ease;
}
.qr-section:hover {
  transform: scale(1.05);
}
</style>
